import { useState } from 'react'

import { t } from '../i18n'
import type { Game, PlayerMe, Slot } from '../types'

interface Props {
  game: Game
  me: PlayerMe
  busy: boolean
  onSet: (spot: number | null, playerId?: number) => void
}

/** Порядок мест на картинке: пара 1 — слева от сетки, пара 2 — справа.
 *  1/3 — левая сторона (revés), 2/4 — правая (drive). */
const PAIRS: [number, number][] = [
  [1, 2],
  [3, 4],
]

function initials(name: string): string {
  return name.trim().slice(0, 1).toUpperCase() || '?'
}

/** Расстановка до матча: каждый сам встаёт на свободное место (так выбирает
 *  партнёра и сторону), капитан может переставить любого — поменять местами. */
export default function CourtSpots({ game, me, busy, onSet }: Props) {
  const [moving, setMoving] = useState<number | null>(null) // кого переставляет капитан
  const joined = game.slots.filter((s) => s.status === 'joined')
  const bySpot = new Map<number, Slot>()
  for (const s of joined) if (s.court_spot) bySpot.set(s.court_spot, s)
  const unplaced = joined.filter((s) => !s.court_spot)
  const mine = joined.find((s) => s.player.id === me.id)
  const open = game.spots_open
  const captain = game.is_captain
  const movingSlot = joined.find((s) => s.player.id === moving)

  function tap(spot: number) {
    if (!open || busy) return
    const occupant = bySpot.get(spot)
    if (captain && moving !== null) {
      if (occupant?.player.id === moving) setMoving(null)
      else {
        onSet(spot, moving)
        setMoving(null)
      }
      return
    }
    if (occupant) {
      if (occupant.player.id === me.id) onSet(null)
      else if (captain) setMoving(occupant.player.id)
      return
    }
    if (mine) onSet(spot)
  }

  const cell = (spot: number, side: string) => {
    const s = bySpot.get(spot)
    const isMe = s?.player.id === me.id
    const selected = s && s.player.id === moving
    const canTake = open && !s && (Boolean(mine) || (captain && moving !== null))
    return (
      <button
        key={spot}
        className={`spot-cell${s ? ' taken' : ''}${isMe ? ' me' : ''}${selected ? ' selected' : ''}${canTake ? ' free' : ''}`}
        disabled={!open || busy || (!s && !canTake && !(captain && moving !== null))}
        onClick={() => tap(spot)}
      >
        {s ? (
          s.player.photo_url ? (
            <img className="face" src={s.player.photo_url} alt="" />
          ) : (
            <span className="face">{initials(s.player.first_name)}</span>
          )
        ) : (
          <span className="face empty">+</span>
        )}
        <span className="spot-name">{s ? s.player.first_name : canTake ? t().spotTake : t().spotEmpty}</span>
        <span className="spot-level">{side}</span>
      </button>
    )
  }

  const pair = (i: number) => (
    <div className="side-wrap">
      <span className="pair-label">{t().pairN(i + 1)}</span>
      <div className="side">
        {cell(PAIRS[i][0], t().spotLeft)}
        {cell(PAIRS[i][1], t().spotRight)}
      </div>
    </div>
  )

  return (
    <div className="court-spots">
      <div className="row between" style={{ marginBottom: 6 }}>
        <span className="title">{t().courtSpotsTitle}</span>
        {movingSlot && <span className="muted small">{t().spotMoving(movingSlot.player.first_name)}</span>}
      </div>
      <div className="court big">
        {pair(0)}
        <div className="net" aria-hidden />
        {pair(1)}
      </div>
      {unplaced.length > 0 && (
        <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 8, alignItems: 'center' }}>
          <span className="muted small">{t().spotUnplaced}:</span>
          {unplaced.map((s) => (
            <button
              key={s.id}
              className="chip"
              aria-pressed={moving === s.player.id}
              disabled={!open || !captain || busy}
              onClick={() => setMoving(moving === s.player.id ? null : s.player.id)}
            >
              {s.player.first_name}
            </button>
          ))}
        </div>
      )}
      {open && (
        <div className="muted small" style={{ marginTop: 8 }}>
          {captain ? t().spotHintCaptain : mine ? t().spotHint : ''}
        </div>
      )}
    </div>
  )
}
